
import { apiFetch } from './api-client'
import { Course, Lesson } from '@/types/supabase'
import type { 
  GetCoursesResponse, 
  GetCourseByIdResponse, 
  GetCourseBySlugResponse, 
  GetCourseLessonsResponse 
} from '@/dto/index'

function mapCourse(dto: any): Course {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description,
    short_description: dto.shortDescription || undefined,
    price: parseFloat(dto.price),
    duration: dto.duration,
    level: dto.level,
    lesson_count: dto.lessonCount,
    image_url: dto.imageUrl,
    created_at: dto.createdAt,
    slug: dto.slug || undefined,
    currency: dto.currency || undefined,
    status: dto.status || undefined,
    format_description: dto.formatDescription || undefined,
    learning_outcomes: dto.learningOutcomes || null,
    target_audience: dto.targetAudience || null,
  };
}

function mapLesson(dto: any): Lesson {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description,
    duration: dto.duration,
    course_id: dto.courseId,
    order: dto.order,
    video_id: dto.videoId,
    pdf_url: dto.pdfUrl,
    created_at: dto.createdAt,
  };
}

// Fetch all available courses
export async function getCourses() {
  try {
    const data = await apiFetch<GetCoursesResponse>('/api/courses')
    return data.courses.map(mapCourse)
  } catch (error) {
    console.error('Error fetching courses:', error)
    return []
  }
}

// Fetch a single course by ID
export async function getCourse(courseId: string) {
  try {
    const data = await apiFetch<GetCourseByIdResponse>(`/api/courses/${courseId}`)
    return mapCourse(data.course)
  } catch (error) {
    console.error('Error fetching course:', error)
    return null
  }
}

// Fetch all lessons for a course
export async function getCourseLessons(courseId: string) {
  try {
    const data = await apiFetch<GetCourseLessonsResponse>(`/api/courses/${courseId}/lessons`)
    return data.lessons.map(mapLesson)
  } catch (error) {
    console.error('Error fetching lessons:', error)
    return []
  }
}

// Fetch a single lesson
export async function getLesson(lessonId: string) {
  try {
    // Note: We need to implement this endpoint or use a filter
    const data = await apiFetch<{ lesson: any }>(`/api/courses/lessons/${lessonId}`)
    return mapLesson(data.lesson)
  } catch (error) {
    console.error('Error fetching lesson:', error)
    return null
  }
}

// Fetch a single course by slug
export async function getCourseBySlug(slug: string) {
  if (!slug) return null;
  try {
    const data = await apiFetch<GetCourseBySlugResponse>(`/api/courses/by-slug/${slug.trim()}`)
    return mapCourse(data.course)
  } catch (error) {
    console.error('Error fetching course by slug:', error)
    return null
  }
}

// Update a course's description (Admin only)
export async function updateCourseDescription(courseId: string, description: string) {
  // This would need a PUT /api/courses/:id endpoint which is not in Step 1.
  // We'll leave it for later or implement if needed for admin features.
  throw new Error("Update not implemented in new API yet");
}
